// Agents list — the Config section's Agents folder landing page (its
// Skills and Memories siblings are sidebar sub-pages). Each orchestration
// group renders its members as a table: id, name, model, created, and last
// updated. Clicking a row opens the agent.
import { useClient } from '@cortex/react-query';
import type { AgentListItem, ModelConfig, OrchestrationGroup, OrgChatConfig } from '@cortex/types';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState, type Dispatch, type SetStateAction } from 'react';

import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { EmptyState } from '../../components/common/EmptyState';
import { Modal } from '../../components/common/Modal';
import { PageHeader } from '../../components/common/PageHeader';
import { Spinner } from '../../components/common/Spinner';
import { routes } from '../../lib/constants';
import { tdStyle, thStyle } from '../../lib/table-styles';

import { groupOrchestrationAgents, type IOrchestrationAgentSection } from './-orchestration-groups';

export const Route = createFileRoute('/agents/')({ component: AgentsPage });

/** Global managed Formant chat agent. It is not an org-owned row, so the list query never returns it. */
const formantChatAgentId = 'agt_formant_chat';
/** Global managed named-bot agent. Same reason it is not in the list query. */
const formantBotAgentId = 'agt_formant_bot';

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

function AgentsPage() {
  const client = useClient();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [groupMenuId, setGroupMenuId] = useState<string | null>(null);
  const [groupDialog, setGroupDialog] = useState<{
    kind: 'rename' | 'clone';
    group: { id: string; name: string };
  } | null>(null);
  const [groupDialogName, setGroupDialogName] = useState('');
  const [deleteGroupTarget, setDeleteGroupTarget] = useState<{
    id: string;
    name: string;
    agentCount: number;
  } | null>(null);

  const agentsQuery = useQuery({
    queryKey: ['agents'],
    queryFn: () => client.http.get('v1/agents').json<IListEnvelope<AgentListItem>>(),
  });
  const modelsQuery = useQuery({
    queryKey: ['model-configs'],
    queryFn: () => client.http.get('v1/model-configs').json<IListEnvelope<ModelConfig>>(),
  });

  const orchestrationGroupsQuery = useQuery({
    queryKey: ['orchestration-groups'],
    queryFn: () => client.http.get('v1/orchestration-groups').json<OrchestrationGroup[]>(),
  });

  const refreshOrchestrationGroups = async () => {
    setGroupMenuId(null);
    setGroupDialog(null);
    setGroupDialogName('');
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['orchestration-groups'] }),
      qc.invalidateQueries({ queryKey: ['agents'] }),
      qc.invalidateQueries({ queryKey: ['persistent-agents'] }),
    ]);
  };
  const updateOrchestrationGroup = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { name?: string } }) =>
      client.http
        .patch(`v1/orchestration-groups/${id}`, { json: patch })
        .json<OrchestrationGroup>(),
    onSuccess: refreshOrchestrationGroups,
  });
  const cloneOrchestrationGroup = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      client.http
        .post(`v1/orchestration-groups/${id}/clone`, { json: { name } })
        .json<OrchestrationGroup>(),
    onSuccess: refreshOrchestrationGroups,
  });
  const deleteOrchestrationGroup = useMutation({
    // The API cascades to every member agent (stopping their live threads)
    // in the same operation.
    mutationFn: (id: string) => client.http.delete(`v1/orchestration-groups/${id}`),
    onSuccess: async () => {
      setDeleteGroupTarget(null);
      await refreshOrchestrationGroups();
    },
  });
  // Per-user star (drives the pinned Favorites section). Optimistic: flip
  // the row locally, reconcile with the server on settle.
  const toggleFavorite = useMutation({
    mutationFn: ({ id, favorite }: { id: string; favorite: boolean }) =>
      favorite
        ? client.http.put(`v1/orchestration-groups/${id}/favorite`, { json: {} })
        : client.http.delete(`v1/orchestration-groups/${id}/favorite`),
    onMutate: async ({ id, favorite }) => {
      await qc.cancelQueries({ queryKey: ['orchestration-groups'] });
      qc.setQueryData<OrchestrationGroup[]>(['orchestration-groups'], (prev) =>
        prev?.map((row) => (row.id === id ? { ...row, isFavorite: favorite } : row)),
      );
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['orchestration-groups'] }),
  });

  const openGroupDialog = (kind: 'rename' | 'clone', group: { id: string; name: string }) => {
    setGroupMenuId(null);
    setGroupDialog({ kind, group });
    setGroupDialogName(kind === 'rename' ? group.name : `${group.name} Copy`);
  };
  const submitGroupDialog = () => {
    if (!groupDialog || !groupDialogName.trim()) return;
    if (groupDialog.kind === 'rename') {
      if (groupDialogName.trim() === groupDialog.group.name) {
        setGroupDialog(null);
        return;
      }
      updateOrchestrationGroup.mutate({
        id: groupDialog.group.id,
        patch: { name: groupDialogName.trim() },
      });
      return;
    }
    cloneOrchestrationGroup.mutate({
      id: groupDialog.group.id,
      name: groupDialogName.trim(),
    });
  };

  const modelIdByConfigId = useMemo(() => {
    const m = new Map<string, string>();
    for (const mc of modelsQuery.data?.items ?? []) m.set(mc.id, mc.modelId);
    return m;
  }, [modelsQuery.data]);

  if (agentsQuery.isLoading) return <Spinner />;
  if (agentsQuery.error) {
    return (
      <div className="alert alert--danger">
        Failed to load agents: {(agentsQuery.error as Error).message}
      </div>
    );
  }

  // Orchestration-kind agents back persistent agents (managed on Automation >
  // Agents > Orchestration — threads, facts, peers, filesystem). They still
  // get a small section here since they're plain Agent rows underneath
  // (same instructions/model/tools editor at routes.agentDetail).
  const allAgents = agentsQuery.data?.items ?? [];
  const orchestrationAgents = allAgents.filter((a) => a.kind === 'ORCHESTRATION');
  const orchestrationSections = groupOrchestrationAgents(
    orchestrationGroupsQuery.data ?? [],
    orchestrationAgents,
  );
  const favoriteOrchestratorSections = orchestrationSections.filter(
    (section) => section.group.isFavorite,
  );
  const otherOrchestratorSections = orchestrationSections.filter(
    (section) => !section.group.isFavorite,
  );

  const openAgent = async (id: string) => {
    try {
      await nav({ to: routes.agentDetail(id) });
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <PageHeader title="Agents" />
      <FormantAssistantTable modelIdByConfigId={modelIdByConfigId} />
      <FormantBotTable modelIdByConfigId={modelIdByConfigId} />
      <Modal
        open={groupDialog !== null}
        onClose={() => {
          if (!updateOrchestrationGroup.isPending && !cloneOrchestrationGroup.isPending) {
            setGroupDialog(null);
          }
        }}
        title={groupDialog?.kind === 'clone' ? 'Clone orchestration group' : 'Rename group'}
        eyebrow={groupDialog?.group.name}
        width={480}
        footer={
          <>
            <button
              className="btn btn--ghost btn--sm"
              disabled={updateOrchestrationGroup.isPending || cloneOrchestrationGroup.isPending}
              onClick={() => setGroupDialog(null)}
            >
              Cancel
            </button>
            <button
              className="btn btn--primary btn--sm"
              disabled={
                !groupDialogName.trim() ||
                updateOrchestrationGroup.isPending ||
                cloneOrchestrationGroup.isPending
              }
              onClick={submitGroupDialog}
            >
              {updateOrchestrationGroup.isPending || cloneOrchestrationGroup.isPending
                ? 'Saving…'
                : groupDialog?.kind === 'clone'
                  ? 'Clone'
                  : 'Rename'}
            </button>
          </>
        }
      >
        <label className="field">
          <span className="field-label">Name</span>
          <input
            className="input"
            value={groupDialogName}
            autoFocus={true}
            onChange={(event) => setGroupDialogName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submitGroupDialog();
              }
            }}
          />
          {groupDialog?.kind === 'clone' && (
            <span className="field-help">
              Copies agent definitions and configuration. Threads are not copied.
            </span>
          )}
        </label>
      </Modal>
      <ConfirmDialog
        open={deleteGroupTarget !== null}
        onClose={() => {
          if (!deleteOrchestrationGroup.isPending) setDeleteGroupTarget(null);
        }}
        onConfirm={() => {
          if (deleteGroupTarget) deleteOrchestrationGroup.mutate(deleteGroupTarget.id);
        }}
        eyebrow={deleteGroupTarget?.name}
        title="Delete orchestration group"
        message={
          deleteGroupTarget && deleteGroupTarget.agentCount > 0
            ? `This deletes ${deleteGroupTarget.agentCount === 1 ? '1 agent' : `${deleteGroupTarget.agentCount} agents`} in this group and stops any of their live threads. This cannot be undone.`
            : 'This group has no agents. This cannot be undone.'
        }
        confirmLabel={deleteOrchestrationGroup.isPending ? 'Deleting…' : 'Delete group'}
        destructive={true}
        pending={deleteOrchestrationGroup.isPending}
      />
      {(updateOrchestrationGroup.error ||
        cloneOrchestrationGroup.error ||
        deleteOrchestrationGroup.error) && (
        <div className="alert alert--danger" style={{ marginBottom: 'var(--sp-3)' }}>
          {
            (
              (updateOrchestrationGroup.error ??
                cloneOrchestrationGroup.error ??
                deleteOrchestrationGroup.error) as Error
            ).message
          }
        </div>
      )}
      {orchestrationSections.length > 0 && (
        <section style={{ marginBottom: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
            {favoriteOrchestratorSections.map((section) => (
              <OrchestrationGroupSection
                key={section.group.id}
                section={section}
                groupMenuId={groupMenuId}
                setGroupMenuId={setGroupMenuId}
                openGroupDialog={openGroupDialog}
                setDeleteGroupTarget={setDeleteGroupTarget}
                updateOrchestrationGroup={updateOrchestrationGroup}
                cloneOrchestrationGroup={cloneOrchestrationGroup}
                toggleFavorite={toggleFavorite}
                modelIdByConfigId={modelIdByConfigId}
                openAgent={openAgent}
              />
            ))}
            {otherOrchestratorSections.map((section) => (
              <OrchestrationGroupSection
                key={section.group.id}
                section={section}
                groupMenuId={groupMenuId}
                setGroupMenuId={setGroupMenuId}
                openGroupDialog={openGroupDialog}
                setDeleteGroupTarget={setDeleteGroupTarget}
                updateOrchestrationGroup={updateOrchestrationGroup}
                cloneOrchestrationGroup={cloneOrchestrationGroup}
                toggleFavorite={toggleFavorite}
                modelIdByConfigId={modelIdByConfigId}
                openAgent={openAgent}
              />
            ))}
          </div>
        </section>
      )}
      {orchestrationSections.length === 0 ? (
        <EmptyState
          title="No agents yet"
          description="Agents are reusable LLM personalities — name, instructions, tools."
          cta={
            <Link to={routes.AGENTS_NEW} className="btn btn--primary btn--sm">
              Create your first agent
            </Link>
          }
        />
      ) : null}
    </>
  );
}

/** One orchestration-group subsection: name, favorite star, edit menu, member rows. */
function OrchestrationGroupSection({
  section,
  groupMenuId,
  setGroupMenuId,
  openGroupDialog,
  setDeleteGroupTarget,
  updateOrchestrationGroup,
  cloneOrchestrationGroup,
  toggleFavorite,
  modelIdByConfigId,
  openAgent,
}: {
  section: IOrchestrationAgentSection;
  groupMenuId: string | null;
  setGroupMenuId: Dispatch<SetStateAction<string | null>>;
  openGroupDialog: (kind: 'rename' | 'clone', group: { id: string; name: string }) => void;
  setDeleteGroupTarget: Dispatch<
    SetStateAction<{ id: string; name: string; agentCount: number } | null>
  >;
  updateOrchestrationGroup: UseMutationResult<
    OrchestrationGroup,
    Error,
    { id: string; patch: { name?: string } }
  >;
  cloneOrchestrationGroup: UseMutationResult<
    OrchestrationGroup,
    Error,
    { id: string; name: string }
  >;
  toggleFavorite: UseMutationResult<unknown, Error, { id: string; favorite: boolean }>;
  modelIdByConfigId: Map<string, string>;
  openAgent: (id: string) => void;
}) {
  const { group } = section;
  return (
    <section>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          marginBottom: 'var(--sp-2)',
        }}
      >
        <h4 className="t-h4" style={{ margin: 0 }}>
          {group.name}
        </h4>
        {group.id !== '__ungrouped__' && (
          <button
            type="button"
            className="btn btn--sm btn--ghost"
            title={group.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            aria-label={group.isFavorite ? 'Unstar group' : 'Star group'}
            aria-pressed={Boolean(group.isFavorite)}
            disabled={toggleFavorite.isPending}
            onClick={() => toggleFavorite.mutate({ id: group.id, favorite: !group.isFavorite })}
            style={{
              color: group.isFavorite ? 'var(--color-warning, #d4a72c)' : 'var(--fg-muted)',
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill={group.isFavorite ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth={1.75}
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 3l2.8 5.7 6.2.9-4.5 4.4 1 6.2-5.5-2.9-5.5 2.9 1-6.2L3 9.6l6.2-.9z" />
            </svg>
          </button>
        )}
        {group.id !== '__ungrouped__' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              marginLeft: 'auto',
            }}
          >
            <Link
              to={routes.AGENTS_NEW}
              search={{ orchestrationGroupId: group.id }}
              className="btn btn--secondary btn--sm"
              title={`Add an agent to ${group.name}`}
            >
              Add agent
            </Link>
            <div style={{ position: 'relative' }}>
              <button
                className="btn btn--secondary btn--sm"
                aria-label={`Actions for ${group.name}`}
                aria-expanded={groupMenuId === group.id}
                onClick={() =>
                  setGroupMenuId((current) => (current === group.id ? null : group.id))
                }
                title={`Manage ${group.name}`}
              >
                Edit
              </button>
              {groupMenuId === group.id && (
                <>
                  <div
                    style={{ position: 'fixed', inset: 0, zIndex: 20 }}
                    onClick={() => setGroupMenuId(null)}
                  />
                  <div
                    className="card"
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: 'calc(100% + 4px)',
                      zIndex: 21,
                      minWidth: 180,
                      padding: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <button
                      className="btn btn--ghost btn--sm"
                      style={{ justifyContent: 'flex-start' }}
                      disabled={updateOrchestrationGroup.isPending}
                      onClick={() => openGroupDialog('rename', { id: group.id, name: group.name })}
                    >
                      Rename
                    </button>
                    <button
                      className="btn btn--ghost btn--sm"
                      style={{ justifyContent: 'flex-start' }}
                      disabled={cloneOrchestrationGroup.isPending}
                      onClick={() => openGroupDialog('clone', { id: group.id, name: group.name })}
                    >
                      Clone
                    </button>
                    <button
                      className="btn btn--ghost btn--sm"
                      style={{ justifyContent: 'flex-start', color: 'var(--danger)' }}
                      onClick={() => {
                        setGroupMenuId(null);
                        setDeleteGroupTarget({
                          id: group.id,
                          name: group.name,
                          agentCount: section.agents.length,
                        });
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      {section.agents.length > 0 ? (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
            <thead>
              <tr>
                <th style={thStyle}>ID</th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Model</th>
                <th style={thStyle}>Created</th>
                <th style={thStyle}>Last updated</th>
              </tr>
            </thead>
            <tbody>
              {section.agents.map((agent) => (
                <AgentRow
                  key={agent.id}
                  id={agent.id}
                  name={agent.name}
                  modelLabel={
                    agent.modelConfigId ? modelIdByConfigId.get(agent.modelConfigId) : undefined
                  }
                  createdAt={agent.createdAt}
                  updatedAt={agent.updatedAt}
                  onOpen={() => openAgent(agent.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="t-sm t-muted">No agents in this group.</div>
      )}
    </section>
  );
}

function FormantBotTable({ modelIdByConfigId }: { modelIdByConfigId: Map<string, string> }) {
  const modelLabel = modelIdByConfigId.get('mc_system_chat') ?? 'Formant default';
  return (
    <section style={{ marginBottom: 'var(--sp-4)' }}>
      <h4 className="t-h4" style={{ margin: '0 0 var(--sp-2)' }}>
        Formant Bot
      </h4>
      <div className="card" style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
          <thead>
            <tr>
              <th style={thStyle}>ID</th>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Model</th>
              <th style={thStyle}>Created</th>
              <th style={thStyle}>Last updated</th>
            </tr>
          </thead>
          <tbody>
            <AgentRow
              id={formantBotAgentId}
              name="Formant Bot"
              modelLabel={modelLabel}
              onOpen={() => undefined}
            />
          </tbody>
        </table>
      </div>
    </section>
  );
}

function FormantAssistantTable({ modelIdByConfigId }: { modelIdByConfigId: Map<string, string> }) {
  const client = useClient();
  const nav = useNavigate();
  const config = useQuery({
    queryKey: ['org-chat-config'],
    queryFn: () => client.http.get('v1/org-chat-config').json<OrgChatConfig>(),
  });
  const overlay = config.data;
  const modelLabel = overlay?.modelConfigId
    ? (modelIdByConfigId.get(overlay.modelConfigId) ?? overlay.modelConfigId)
    : 'Formant default';

  return (
    <section style={{ marginBottom: 'var(--sp-4)' }}>
      <h4 className="t-h4" style={{ margin: '0 0 var(--sp-2)' }}>
        Formant AI Assistant
      </h4>
      <div className="card" style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
          <thead>
            <tr>
              <th style={thStyle}>ID</th>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Model</th>
              <th style={thStyle}>Created</th>
              <th style={thStyle}>Last updated</th>
            </tr>
          </thead>
          <tbody>
            <AgentRow
              id={formantChatAgentId}
              name="Formant AI Assistant"
              modelLabel={modelLabel}
              createdAt={overlay?.createdAt}
              updatedAt={overlay?.updatedAt}
              onOpen={() => {
                nav({ to: routes.AGENTS_ASSISTANT }).catch((err) => console.error(err));
              }}
            />
          </tbody>
        </table>
      </div>
    </section>
  );
}

const monthDayFmt = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });

function formatMonthDay(value: Date | string): string {
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : monthDayFmt.format(d);
}

function AgentRow({
  id,
  name,
  modelLabel,
  createdAt,
  updatedAt,
  onOpen,
}: {
  id: string;
  name: string;
  modelLabel: string | undefined;
  createdAt?: Date | string | undefined;
  updatedAt?: Date | string | undefined;
  onOpen: () => void;
}) {
  return (
    <tr
      onClick={onOpen}
      style={{ cursor: 'pointer', borderTop: '1px solid var(--border-subtle)' }}
      onMouseEnter={(e) => {
        for (const td of e.currentTarget.children)
          (td as HTMLElement).style.background = 'rgba(44,65,66,0.08)';
      }}
      onMouseLeave={(e) => {
        for (const td of e.currentTarget.children) (td as HTMLElement).style.background = '';
      }}
    >
      <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', fontSize: 12 }}>{id}</td>
      <td style={{ ...tdStyle, fontWeight: 500, color: 'var(--text-primary)' }}>{name}</td>
      <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>
        {modelLabel ?? <span className="t-muted">—</span>}
      </td>
      <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>
        {createdAt ? formatMonthDay(createdAt) : '—'}
      </td>
      <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>
        {updatedAt ? formatMonthDay(updatedAt) : '—'}
      </td>
    </tr>
  );
}
